/**
 * useBranchSession.ts — Resolves branchId from auth context and manages
 * cash register (boxNumber) selection for the POS session.
 *
 * Branch resolution order:
 *   1. Parse "branch:{id}" from user's Cognito groups
 *   2. Without assigned branches the route guard prevents entry.
 *
 * Box number:
 *   - Persisted in sessionStorage so a page refresh keeps the same caja
 *   - Default = 1 if no multi-register selection is needed
 *
 * C# equivalent: Session["BranchId"], Session["CajaId"]
 */

import { useState, useMemo, useCallback } from 'react';
import { useAuthOptional } from '../lib/auth';
import type { BranchInfo } from '../lib/auth';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Cognito group prefix — matches API middleware branch.ts */
const BRANCH_GROUP_PREFIX = 'branch:';

/** SessionStorage key for persisted box selection */
const BOX_STORAGE_KEY = 'kapi:pos:boxNumber';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract branch IDs from Cognito groups.
 * Groups matching "branch:{id}" → el id (VARCHAR, puede ser alfanumérico).
 *
 * @example
 * extractBranchIds(['admin', 'branch:003', 'branch:52_1'])
 * // → ['003', '52_1']
 */
function extractBranchIds(groups: string[]): string[] {
  return groups
    .filter((g) => g.startsWith(BRANCH_GROUP_PREFIX))
    .map((g) => g.slice(BRANCH_GROUP_PREFIX.length))
    .filter((s) => s.length > 0);
}

/**
 * Read persisted box number from sessionStorage.
 * Returns null if not set or invalid.
 */
function readPersistedBox(): number | null {
  try {
    const stored = sessionStorage.getItem(BOX_STORAGE_KEY);
    if (!stored) return null;
    const n = Number(stored);
    return Number.isInteger(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

/**
 * Persist box number to sessionStorage.
 */
function persistBox(box: number): void {
  try {
    sessionStorage.setItem(BOX_STORAGE_KEY, String(box));
  } catch {
    // Silently ignore storage errors
  }
}

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface BranchSession {
  /** Resolved branch ID = company_branches.ID (VARCHAR; '003', '52_1', …) */
  branchId: string;
  /** Nombre de la sucursal activa (para mostrar en el header) */
  branchName: string;
  /** Selected cash register number */
  boxNumber: number;
  /** Whether the box has been explicitly selected by the user */
  boxSelected: boolean;
  /** Whether the user has access to multiple branches */
  isMultiBranch: boolean;
  /**
   * True si ya hay una sucursal definida para la sesión: porque el usuario la
   * eligió, o porque solo tiene una. False ⇒ multi-sucursal sin elegir (el gate
   * debe mostrar el selector antes de dejar entrar).
   */
  branchSelected: boolean;
  /** All branch IDs the user has access to (VARCHAR ids) */
  availableBranches: string[];
  /** Lista de sucursales (nombre, empresa, logo, default) para el selector */
  branchList: BranchInfo[];
  /** Elegir la sucursal activa de la sesión */
  setBranchId: (id: string) => void;
  /** Update the selected cash register */
  setBoxNumber: (box: number) => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useBranchSession — resolves branch context from auth and manages
 * cash register selection state.
 *
 * Usage:
 * ```tsx
 * const { branchId, boxNumber, boxSelected, setBoxNumber } = useBranchSession();
 * ```
 */
export function useBranchSession(): BranchSession {
  // useAuthOptional: no truena si se renderiza fuera de <AuthProvider> (tests,
  // chrome compartido). Defaults defensivos si falta el contexto.
  const auth = useAuthOptional();
  const user = auth?.user ?? null;
  const branches = auth?.branches;
  const selectedBranchId = auth?.selectedBranchId ?? null;
  const setBranch = useCallback(
    (id: string) => { auth?.setBranch?.(id); },
    [auth],
  );

  // Lista de sucursales con nombre. Fuente: el contexto (login). Si falta (sesión
  // vieja sin la lista persistida), se deriva de los grupos del token (id=name).
  const branchList = useMemo<BranchInfo[]>(() => {
    const fromCtx = (branches as BranchInfo[] | undefined) ?? [];
    if (fromCtx.length > 0) {
      return fromCtx.map((b) => ({
        id: b.id,
        name: b.name || b.id,
        companyName: b.companyName ?? '',
        logo: b.logo ?? '',
        db: b.db ?? '',
        isDefault: Boolean(b.isDefault),
      }));
    }
    const ids = user ? extractBranchIds(user.groups ?? []) : [];
    return ids.map((id) => ({ id, name: id, companyName: '', logo: '', db: '', isDefault: false }));
  }, [branches, user]);

  const availableBranches = useMemo(() => branchList.map((b) => b.id), [branchList]);
  const isMultiBranch = availableBranches.length > 1;

  // La selección solo cuenta si pertenece a las sucursales del usuario.
  const validSelected =
    selectedBranchId && availableBranches.includes(selectedBranchId) ? selectedBranchId : null;

  // Resolve primary branchId
  const branchId = useMemo(() => {
    if (validSelected) return validSelected;
    if (availableBranches.length >= 1 && availableBranches[0] !== undefined) {
      // Una sola sucursal → esa; varias sin elegir → la primera como fallback
      // (el gate evita usar la app hasta que el usuario elija).
      return availableBranches[0];
    }
    // Una sesión sin sucursales no puede operar.
    return '';
  }, [validSelected, availableBranches]);

  // branchSelected: ya hay sucursal definida (elegida, o única).
  const branchSelected = Boolean(validSelected) || availableBranches.length === 1;

  const branchName = useMemo(
    () => branchList.find((b) => b.id === branchId)?.name ?? branchId,
    [branchList, branchId],
  );

  // Box number state — initialized from sessionStorage or default
  const [boxState, setBoxState] = useState<{ box: number; selected: boolean }>(() => {
    const persisted = readPersistedBox();
    return persisted !== null
      ? { box: persisted, selected: true }
      : { box: 1, selected: false };
  });

  const setBoxNumber = useCallback((box: number) => {
    persistBox(box);
    setBoxState({ box, selected: true });
  }, []);

  return {
    branchId,
    branchName,
    boxNumber: boxState.box,
    boxSelected: boxState.selected,
    isMultiBranch,
    branchSelected,
    availableBranches,
    branchList,
    setBranchId: setBranch,
    setBoxNumber,
  };
}
